import { datadogRum, type RumErrorEvent } from '@datadog/browser-rum';
import { initBrowserRum as _initBrowserRum } from '@smg-real-estate/browser-rum';

const CEFSHARP_BOT_ERROR_REGEX = /Object Not Found Matching Id:\d+, MethodName:\w+, ParamCount:\d+/;
const AUTOFILL_HANDLER_ERROR_REGEX = /_AutofillCallbackHandler/;

/** Mirrors browser-rum getTrackingConsent(); re-applied because v1.5.3 listens to OneTrust only once. */
function syncRumTrackingConsent(): void {
  const consent = document.cookie.match(/(^| )OptanonConsent=([^;]+)/);
  const granted = decodeURIComponent(consent?.[2] ?? '').includes('C0001:1');
  datadogRum.setTrackingConsent(granted ? 'granted' : 'not-granted');
}

export const initBrowserRum = (): void => {
  const releaseVersion = import.meta.env.VUE_APP_RUM_RELEASE_VERSION;
  // Enable Datadog RUM only if there is a release version (prod & test environments)
  if (!releaseVersion) return;

  const allowedTracingUrls = [
    import.meta.env.VUE_APP_PRICING_API_BASE_URL,
    import.meta.env.VUE_APP_INSERTION_FUNNEL_API_BASE_URL,
  ].filter((url): url is string => Boolean(url));

  _initBrowserRum({
    applicationId: import.meta.env.VUE_APP_DATADOG_APPLICATION_ID,
    clientToken: import.meta.env.VUE_APP_DATADOG_CLIENT_TOKEN,
    service: `${import.meta.env.VUE_APP_BUILD_TARGET}-insertion-funnel`,
    env: import.meta.env.VUE_APP_STAGE,
    // Set session sample rate to 100% for test environment
    sessionSampleRate: 100,
    sessionReplaySampleRate: 0,
    version: releaseVersion,
    allowedTracingUrls,
    trackingConsentEnabled: true,
    beforeSend: (event) => {
      if (event.type === 'error') {
        const error = (event as RumErrorEvent).error;
        // filter out errors caused by CefSharp-based bots (e.g. Outlook SafeLinks pre-fetch)
        // these bots are prefetching Landing Page links we sent in emails
        // https://github.com/DataDog/browser-sdk/issues/2715
        if (CEFSHARP_BOT_ERROR_REGEX.test(error.message)) return false;
        // filter injected-script noise from Meta in-app browsers (Instagram/Facebook on iOS).
        // Their webview injects autofill JS that calls `_AutofillCallbackHandler`, which is not
        // defined in our app; it does not affect site functionality and is widely ignored in RUM.
        // https://github.com/getsentry/sentry-javascript/issues/9216 (see ignoreErrors: _AutofillCallbackHandler)
        // https://developers.facebook.com/community/threads/320013549791141/
        if (
          AUTOFILL_HANDLER_ERROR_REGEX.test(error.message) ||
          (error.stack && AUTOFILL_HANDLER_ERROR_REGEX.test(error.stack))
        )
          return false;
      }
      return true;
    },
  });

  if (!import.meta.env.SSR) {
    syncRumTrackingConsent();
    window.addEventListener('OneTrustGroupsUpdated', syncRumTrackingConsent);
  }
};

export function trackFeatureFlagsInRum(
  treatments: Record<string, { treatment?: string }> | undefined,
): void {
  if (!treatments) return;

  Object.entries(treatments).forEach(([flagKey, { treatment }]) => {
    if (treatment === undefined) return;
    datadogRum.addFeatureFlagEvaluation(flagKey, treatment);
  });
}

/** Low-cardinality state of the insertion being viewed, stored in `@context.*`. */
export interface RumViewContext {
  user_type?: string;
  canton?: string;
  offer_type?: string;
  category_group?: string;
  is_shared_apartment?: boolean;
  insertion_status?: string;
  bundle_tier?: string;
  bundle_segment?: string;
  bundle_period_days?: number;
  crosslisting_selected?: boolean;
  payment_type?: string;
}

const isRumInitialized = (): boolean => datadogRum.getInitConfiguration() !== undefined;

/** Replaces the whole view context; `startView` resets it on every navigation. */
export function setRumViewContext(context: RumViewContext): void {
  if (!isRumInitialized()) return;

  datadogRum.setViewContext({ ...context });
}
